import type { JSX } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Company, CompanySize, CompanyStatus } from '../types/company.types';
import { CompanyLogo } from './CompanyLogo';
import { CompanyActions } from './CompanyActions';

export interface CompanyCardProps {
  company: Company;
  onEdit: (company: Company) => void;
  onActivate?: ((id: string) => void) | undefined;
  onSuspend?: ((id: string) => void) | undefined;
  onEnterCompany?: ((company: Company) => void) | undefined;
  showStatusActions?: boolean | undefined;
}

const sizeLabels: Record<CompanySize, string> = {
  SMALL: '1-50',
  MEDIUM: '51-200',
  LARGE: '201-1000',
  ENTERPRISE: '1000+',
};
const statusConfig: Record<
  CompanyStatus,
  { variant: 'default' | 'destructive' | 'secondary'; label: string }
> = {
  ACTIVE: { variant: 'default', label: 'Active' },
  SUSPENDED: { variant: 'destructive', label: 'Suspended' },
  DELETED: { variant: 'secondary', label: 'Deleted' },
};

function CompanyLocation({
  city,
  country,
}: {
  city?: string | undefined;
  country?: string | undefined;
}): JSX.Element | null {
  const loc = [city, country].filter(Boolean).join(', ');
  return loc ? <p className="text-sm text-muted-foreground">{loc}</p> : null;
}

export function CompanyCard({
  company,
  onEdit,
  onActivate,
  onSuspend,
  onEnterCompany,
  showStatusActions = false,
}: CompanyCardProps): JSX.Element {
  const { variant, label } = statusConfig[company.status];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <CompanyLogo company={company} />
          <div className="flex flex-col gap-1">
            <button
              onClick={() => onEnterCompany?.(company)}
              className="font-semibold text-lg hover:text-primary hover:underline text-left"
              data-testid={`company-name-${company.id}`}
            >
              {company.name}
            </button>
            <p className="text-sm text-muted-foreground">{company.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={variant}>{label}</Badge>
          <CompanyActions
            company={company}
            onEdit={onEdit}
            onSuspend={onSuspend}
            onActivate={onActivate}
            showStatusActions={showStatusActions}
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2">
          {company.industry && <p className="text-sm">{company.industry}</p>}
          <CompanyLocation city={company.city} country={company.country} />
          {company.companySize && (
            <Badge variant="outline" className="w-fit">
              {sizeLabels[company.companySize]}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
